import { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useViewContext } from '@/providers/Global'
import { DEFAULT_RECT, type IAppViewContext, type IAppViewState } from '@/services/View'

// ///////////////////////
// MARK: EXPORTS
export function useViewDomRect<T extends HTMLElement>(
  view?: keyof IAppViewState,
): [
  setRef: React.Dispatch<React.SetStateAction<T | null>>,
  result: DOMRectReadOnly,
  ref: T | null,
] {
  const View = useViewContext()

  const [element, setElement] = useState<T | null>(null)

  const [result, setResult] = useState<DOMRectReadOnly>(DEFAULT_RECT)

  const debounceSet = useDebouncedCallback((...args: Parameters<IAppViewContext['set']>) => {
    View.set(...args)
  }, 300)

  useEffect(() => {
    if (element) {
      const observer = new ResizeObserver((entries) => {
        if (entries.length > 0) {
          const entry = entries[0]

          if (view) {
            setResult({
              ...entry.contentRect,
              top: entry.contentRect.top,
              bottom: entry.contentRect.bottom,
              left: entry.contentRect.left,
              right: entry.contentRect.right,
              x: entry.contentRect.x,
              y: entry.contentRect.y,
              width: entry.contentRect.width,
              height: entry.contentRect.height,
            })

            debounceSet(view, {
              ...entry.contentRect,
              top: entry.contentRect.top,
              bottom: entry.contentRect.bottom,
              left: entry.contentRect.left,
              right: entry.contentRect.right,
              x: entry.contentRect.x,
              y: entry.contentRect.y,
              width: entry.contentRect.width,
              height: entry.contentRect.height,
              ref: entry.target,
            })
          }
        }
      })

      observer.observe(element)

      return () => {
        observer.disconnect()

        setResult(DEFAULT_RECT)
      }
    }
  }, [debounceSet, element, view])

  return [setElement, view ? View[view] : result, element]
}
